import { IonTextarea } from '@ionic/react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ContactTargetType } from '../../../lib/apollo/types'
import { useCreateContactRequestMutation } from '../../../lib/apollo/types'
import { useUser } from '../../../providers/Auth/hooks'
import useToast from '../../../providers/Toast/hooks'
import Button from '../../Button'
import Buttons from '../../Buttons'
import Modal from '../../Modal'
import ContactPartner from '../ContactPartner'

import './style.scss'

/** Spiegelt `MAX_MESSAGE_LENGTH` der Middleware — dort wird es erzwungen. */
export const MAX_CONTACT_MESSAGE_LENGTH = 500

interface ContactRequestComposerProps {
    isOpen: boolean
    onClose: () => void
    targetType: ContactTargetType
    targetId: string
    /**
     * Name und Bild des Ziels. Die Aufrufer haben beides bereits geladen —
     * ohne sie schreibt man ins Leere, mit ihnen sieht man, an wen.
     */
    targetName?: string | null
    targetImage?: string | null
    onSent?: (conversationId: string) => void
}

/**
 * Die erste Nachricht ist immer eine Kontaktanfrage — der Chat oeffnet sich
 * erst, wenn die Gegenseite zustimmt. Der Hinweis dazu steht bewusst im
 * Composer, damit niemand eine sofortige Antwort erwartet.
 */
const ContactRequestComposer: React.FC<ContactRequestComposerProps> = ({
    isOpen,
    onClose,
    targetType,
    targetId,
    targetName,
    targetImage,
    onSent,
}) => {
    const { t } = useTranslation()
    const [present] = useToast()
    const [message, setMessage] = useState('')
    const user = useUser()

    const [createContactRequest, { loading }] = useCreateContactRequestMutation({
        onError: () => {
            present(
                t('contact.request.failed', 'Die Anfrage konnte nicht gesendet werden.'),
                'danger',
            )
        },
    })

    const close = useCallback(() => {
        setMessage('')
        onClose()
    }, [onClose])

    const send = useCallback(async () => {
        const body = message.trim()
        if (!body) return

        const { data } = await createContactRequest({
            variables: {
                input: {
                    targetType,
                    targetId,
                    message: body,
                },
            },
        })

        if (!data) return

        present(
            t('contact.request.sent', 'Deine Anfrage wurde gesendet.'),
            'success',
        )
        setMessage('')
        onClose()
        onSent?.(data.createContactRequest.conversationId)
    }, [
        message,
        createContactRequest,
        targetType,
        targetId,
        present,
        t,
        onClose,
        onSent,
    ])

    const length = message.trim().length
    const tooLong = length > MAX_CONTACT_MESSAGE_LENGTH

    return (
        <Modal
            modalTitle={t('contact.request.title', 'Kontakt aufnehmen')}
            isOpen={isOpen}
            onDidDismiss={close}
            onClose={close}
            size='alert'
        >
            <div className='contact-composer ion-padding-horizontal'>
                {targetName && (
                    <div className='contact-composer__party'>
                        <div className='contact-composer__label'>
                            {t('contact.composer.to', 'An')}
                        </div>
                        <ContactPartner
                            name={targetName}
                            image={targetImage}
                        />
                    </div>
                )}

                {user?.user && (
                    <div className='contact-composer__party'>
                        <div className='contact-composer__label'>
                            {t('contact.composer.from', 'Von')}
                        </div>
                        <ContactPartner
                            size={32}
                            name={`${user.user.firstName} ${user.user.lastName}`}
                            image={user.user.image}
                        />
                    </div>
                )}

                <p className='n2br contact-composer__hint'>
                    {t(
                        'contact.request.hint',
                        'Deine erste Nachricht ist eine Kontaktanfrage. Der Chat wird freigeschaltet, sobald sie angenommen wurde.',
                    )}
                </p>
                <IonTextarea
                    className='contact-composer__input'
                    rows={6}
                    value={message}
                    maxlength={MAX_CONTACT_MESSAGE_LENGTH}
                    placeholder={t(
                        'contact.request.placeholder',
                        'Deine Nachricht',
                    )}
                    // Ionic 7 meldet `ionChange` beim Textarea erst beim
                    // Verlassen des Feldes — der Zaehler stuende dann still und
                    // der Senden-Knopf bliebe waehrend des Tippens deaktiviert.
                    onIonInput={(event) => {
                        setMessage(event.detail.value ?? '')
                    }}
                />
                <div className='contact-composer__counter'>
                    {length} / {MAX_CONTACT_MESSAGE_LENGTH}
                </div>
            </div>
            <Buttons>
                <Button
                    fill='solid'
                    color='secondary'
                    loading={loading}
                    disabled={!message.trim() || tooLong || loading}
                    onClick={send}
                >
                    {t('contact.request.send', 'Anfrage senden')}
                </Button>
                <Button
                    fill='clear'
                    onClick={close}
                >
                    {t('buttons.cancel')}
                </Button>
            </Buttons>
        </Modal>
    )
}

export default ContactRequestComposer
